import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, switchMap, takeWhile, tap } from 'rxjs';
import { environment } from '../../environments/environment.development';

export interface PaymentRequest {
    id_usuario?: number;
    id_plan: number;
    referencia_transaccion: string;
    monto: number;
    metodo_pago: string;
    id_link?: string | null;
    digits_card: string;
}

export interface PaymentData {
    id_pago: number;
    id_usuario: number;
    id_plan: number;
    monto: number;
    metodo_pago: string;
    estado: 'Exitoso' | 'Fallido' | 'Reembolsado' | 'Pendiente';
    referencia_transaccion: string;
    fecha_pago: string;
    id_suscripcion?: number | null;
    iso_response_code?: string | null;
    id_link?: string | null;
}

export interface CreatePaymentResponse {
    ok: boolean;
    msg: string;
    data: PaymentData;
}

export interface PaymentResponse {
    id_pago: number;
    id_suscripcion: number | null;
    id_usuario: number;
    id_plan: number;
    monto: number;
    fecha_pago: Date;
    metodo_pago: string;
    estado: 'Exitoso' | 'Fallido' | 'Reembolsado' | 'Pendiente';
    referencia_transaccion: string | null;
    iso_response_code: string | null;
    id_link: string | null;
}

export interface PaymentStatusResponse {
    ok: boolean;
    pago?: PaymentResponse; // Mantener compatibilidad anterior si existe
    data?: PaymentResponse; // Nueva estructura
}

export interface ApayTransactionRequest {
    monto: number;
    card_number: string;
    cvv: string;
    exp_date: string;
    cardholderName: string;
    token: string;
    ambiente: string;
    metodo: number;
}

export interface ApayTransactionResponse {
    id: string;
    url: string;
    msg?: string;
    idLink?: string;
}

export interface ApiPlanCurso {
    id_curso: number;
    titulo: string;
    descripcion: string;
    es_premium: boolean;
    estado: string;
}

export interface ApiPlan {
    id_plan: number;
    nombre_plan: string;
    precio: string;
    duracion_dias: number;
    descripcion: string;
    activo: boolean;
    es_default: boolean;
    cursos: ApiPlanCurso[];
}

export interface PlanesResponse {
    ok: boolean;
    data: ApiPlan[];
}

/** NOWPayments — crear intención de pago (registro con cripto) */
export interface NowPaymentsCreateRequest {
    email: string;
    id_plan: number;
    pay_currency?: string;
    order_description?: string;
}

export interface NowPaymentsCreateResponse {
    ok: boolean;
    msg: string;
    id_pago: number;
    order_id?: string;
    payment_id?: string;
    pay_address: string;
    pay_amount: string;
    pay_currency: string;
    price_amount: number;
    price_currency: string;
    invoice_url: string | null;
    payment_status?: string;
}

/** Estado público del pago NOWPayments (polling) */
export interface NowPaymentsPagoStatusResponse {
    ok: boolean;
    id_pago: number;
    estado: string;
    puede_registrarse: boolean;
    id_plan?: number;
    msg?: string;
}

@Injectable({
    providedIn: 'root'
})
export class PaymentService {
    private readonly API_URL = environment.apiUrl;
    private readonly APAY_URL = 'https://aventu-back-end.com/apay-api-transactor/';
    private readonly APAY_TOKEN = 'b5d88e98-aad5-4f68-8b08-15186cc3d8d7';

    constructor(private http: HttpClient) { }

    /**
     * Obtiene los planes disponibles desde la API
     */
    getPlanes(): Observable<PlanesResponse> {
        return this.http.get<PlanesResponse>(`${this.API_URL}/planes`);
    }

    /**
     * Crea un nuevo pago en el sistema
     */
    createPayment(paymentData: PaymentRequest): Observable<CreatePaymentResponse> {
        return this.http.post<CreatePaymentResponse>(`${this.API_URL}/payments/newPaymentTransaction`, paymentData);
    }

    /**
     * Verifica el estado de un pago
     */
    checkPaymentStatus(transactionId: string): Observable<PaymentStatusResponse> {
        return this.http.get<PaymentStatusResponse>(`${this.API_URL}/payments/getPaymentsByTransactionId/${transactionId}`);
    }

    /**
     * Inicia el polling para verificar el estado del pago cada 1 segundo
     * Se detiene cuando el estado es diferente de 'Pendiente'
     */
    pollPaymentStatus(
        transactionId: string,
        onUpdate: (payment: PaymentResponse) => void
    ): Observable<PaymentStatusResponse> {
        return interval(1000).pipe(
            switchMap(() => this.checkPaymentStatus(transactionId)),
            tap(response => {
                // Verificar si la respuesta es válida y tiene los datos del pago
                if (response.ok && response.data) {
                    // Mapear la respuesta al formato interno PaymentResponse si es necesario
                    // Asumimos que response.data cumple con la estructura necesaria
                    // Ajuste temporal para manejar la estructura de respuesta mostrada: { ok: true, data: { ... } }
                    // La interfaz PaymentStatusResponse espera { ok: boolean, pago: PaymentResponse }
                    // Pero el ejemplo muestra { ok: true, data: { ... } }
                    // Vamos a adaptar el uso en el componente o ajustar aquí si es posible.
                    // Dado que el componente espera `response.pago`, y la API devuelve `response.data`,
                    // lo ideal es ajustar la interfaz o mapearlo aquí.
                    
                    // Como no puedo cambiar la interfaz Response fácilmente sin ver donde más se usa,
                    // y el componente usa `payment.estado`, voy a asumir que `response.data` es lo que se pasa.
                    // Pero Typescript se quejará si no coinciden.
                    
                    // Voy a emitir `response.data` como si fuera `payment` al callback `onUpdate`.
                    // Nota: El componente espera PaymentResponse.
                    
                    onUpdate(response.data as unknown as PaymentResponse);
                } else if (response.ok && response.pago) {
                    onUpdate(response.pago);
                }
            }),
            takeWhile(response => {
                // Continuar mientras el estado sea 'Pendiente'
                // Adaptar para leer 'data' o 'pago'
                const paymentData = (response as any).data || response.pago;
                return response.ok && paymentData && paymentData.estado === 'Pendiente';
            }, true) // true para incluir el último valor que cumple la condición (el que ya no es Pendiente)
        );
    }

    /**
     * Procesa la transacción con APay
     */
    processApayTransaction(transactionData: ApayTransactionRequest): Observable<ApayTransactionResponse> {
        const formData = new URLSearchParams();
        formData.append('monto', transactionData.monto.toString());
        formData.append('card_number', transactionData.card_number);
        formData.append('cvv', transactionData.cvv);
        formData.append('exp_date', transactionData.exp_date);
        formData.append('cardholderName', transactionData.cardholderName);
        formData.append('token', transactionData.token);
        formData.append('ambiente', transactionData.ambiente);
        formData.append('metodo', transactionData.metodo.toString());

        return this.http.post<ApayTransactionResponse>(this.APAY_URL, formData.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
    }

    /**
     * Detecta el tipo de tarjeta basado en el número
     */
    detectCardType(cardNumber: string): string {
        const cleanNumber = cardNumber.replace(/\s/g, '');

        // Visa
        if (/^4/.test(cleanNumber)) {
            return 'Visa';
        }
        // Mastercard
        if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber)) {
            return 'Mastercard';
        }
        // American Express
        if (/^3[47]/.test(cleanNumber)) {
            return 'American Express';
        }
        // Discover
        if (/^6(?:011|5)/.test(cleanNumber)) {
            return 'Discover';
        }

        return 'Otra';
    }

    /**
     * Actualiza el usuario asociado a un pago
     */
    updatePaymentUser(data: { id_usuario: number; referencia_transaccion: string }): Observable<any> {
        return this.http.post(`${this.API_URL}/payments/updatePaymentUser`, data);
    }
    getApayToken(): string {
        return this.APAY_TOKEN;
    }

    /**
     * Crea intención de pago NOWPayments (sin JWT).
     */
    createNowPaymentsPayment(body: NowPaymentsCreateRequest): Observable<NowPaymentsCreateResponse> {
        return this.http.post<NowPaymentsCreateResponse>(
            `${this.API_URL}/payments/nowpayments/create`,
            body
        );
    }

    /**
     * Consulta estado del pago cripto (email debe coincidir con el del create).
     */
    getNowPaymentsPagoStatus(idPago: number, email: string): Observable<NowPaymentsPagoStatusResponse> {
        const q = encodeURIComponent(email);
        return this.http.get<NowPaymentsPagoStatusResponse>(
            `${this.API_URL}/payments/nowpayments/pago/${idPago}?email=${q}`
        );
    }
}
